/**
 * DebtPlanContext — one `GET /api/debt-plan` for the whole Liabilities tab.
 *
 * The page is now ordered as: numbers → what you owe → your payoff path →
 * make the plan work harder. Those sections are far apart in the layout but
 * must never disagree, so they all read from this single fetch.
 *
 * Progressive loading: the last completed plan is hydrated synchronously from
 * the page cache, so a repeat visit shows real numbers on the first frame and
 * marks itself "Updating…" while a fresh one is calculated. Nothing usable is
 * ever replaced by an empty skeleton.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { fetchDebtPlan } from '@/lib/debtPlanApi';
import { useAuth } from '@/context/AuthContext';
import { readCache, writeCache } from '@/lib/pageCache';

const Ctx = createContext(null);
const cacheKey = (group) => `debt-plan${group ? `:${group}` : ''}`;

export function DebtPlanProvider({ children, group = '' }) {
  const { user } = useAuth();
  const uid = user?.id;
  const [plan, setPlan] = useState(() => readCache(uid, cacheKey(group)));
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(true);
  const [extra, setExtra] = useState('');
  const [nonce, setNonce] = useState(0);
  const reqRef = useRef(0);

  useEffect(() => {
    const n = Number(extra);
    const amount = Number.isFinite(n) && n > 0 ? n : 0;
    const id = ++reqRef.current;
    setBusy(true);
    // A scoped sub-tab has its own cached result; show it while the new one loads.
    if (!amount) {
      const cached = readCache(uid, cacheKey(group));
      if (cached) setPlan(cached);
    }
    const t = setTimeout(async () => {
      const data = await fetchDebtPlan({ extraMonthly: amount, group, fresh: nonce > 0 });
      if (id !== reqRef.current) return;          // a newer request won
      setError(!data);
      if (data) {
        setPlan(data);
        if (!amount) writeCache(uid, cacheKey(group), data);
      }
      setBusy(false);
    }, extra ? 450 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extra, group, nonce, uid]);

  useEffect(() => {
    const onChanged = () => setNonce((v) => v + 1);
    window.addEventListener('accounts:changed', onChanged);
    return () => window.removeEventListener('accounts:changed', onChanged);
  }, []);

  const value = {
    plan, busy, error, extra, setExtra,
    refreshing: busy && !!plan,
    retry: () => { setError(false); setNonce((v) => v + 1); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDebtPlan() {
  return useContext(Ctx)
    || { plan: null, busy: false, error: false, refreshing: false, extra: '', retry: () => {} };
}

export default DebtPlanProvider;
